<template>
  <div v-if="isVisible" :class="$style.dialogOverlay" @click.self="handleOverlayClick">
    <div :class="$style.dialogContainer">
      <div :class="$style.headerSection">
        <h3 :class="$style.title">상품 선택하기</h3>
        <button :class="$style.closeButton" @click="closeModal" aria-label="닫기">✕</button>
      </div>

      <div :class="$style.contentSection">
        <p :class="$style.description">문의하실 상품을 선택해 주세요.</p>

        <div v-if="loading && !categories.length" :class="$style.loadingState">
          <p>카테고리 정보를 불러오는 중입니다...</p>
          <div :class="$style.spinner"></div>
        </div>
        <div v-else-if="error && !categories.length" :class="$style.errorState">
          카테고리 로딩 중 오류: {{ error.message }}
        </div>

        <div v-if="categories.length > 0" :class="$style.categorySelectionArea">
          <div :class="$style.formGroup">
            <label for="level1Category" :class="$style.formLabel">최상위 카테고리</label>
            <select id="level1Category" v-model="selectedLevel1CategoryId" :class="$style.selectMenu">
              <option :value="undefined" disabled>최상위 카테고리 선택</option>
              <option v-for="cat in level1CategoryOptions" :key="cat.id" :value="cat.id">
                {{ cat.name }}
              </option>
            </select>
          </div>

          <div v-if="selectedLevel1CategoryId && level2CategoryOptions.length > 0" :class="$style.formGroup">
            <label for="level2Category" :class="$style.formLabel">하위 카테고리</label>
            <select id="level2Category" v-model="selectedLevel2CategoryId" :class="$style.selectMenu">
              <option :value="undefined" disabled>하위 카테고리 선택</option>
              <option v-for="cat in level2CategoryOptions" :key="cat.id" :value="cat.id">
                {{ cat.name }}
              </option>
            </select>
          </div>

          <div v-if="selectedLevel2CategoryId && level3CategoryOptions.length > 0" :class="$style.formGroup">
            <label for="level3Category" :class="$style.formLabel">세부 카테고리</label>
            <select id="level3Category" v-model="selectedLevel3CategoryId" :class="$style.selectMenu">
              <option :value="undefined" disabled>세부 카테고리 선택</option>
              <option v-for="cat in level3CategoryOptions" :key="cat.id" :value="cat.id">
                {{ cat.name }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div :class="$style.footerSection">
        <button :class="[$style.button, $style.cancelButton]" @click="closeModal">취소</button>
        <button
          :class="[$style.button, $style.submitButton]"
          :disabled="!finalSelectedCategoryInfo"
          @click="submitSelection"
        >
          선택 완료
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, useCssModule } from "vue"
import { useProductCategory, type ProductCategory } from "@/composables/useProductCategory"

// ProductCategory 인터페이스 (productCategory.store.ts 에서 가져옴)
// interface ProductCategory {
//     id: string;
//     name: string;
//     productNo?: string | number | null;
//     order: number;
//     parentId: string | null;
//     // storeId?: string; // chatbot-widget 에서는 storeId 개념이 없을 수 있음
//     createdAt?: any; // Firestore Timestamp or Date
//     updatedAt?: any; // Firestore Timestamp or Date
// }

export interface SelectProductModalResult {
  selectedProduct?: { name: string; productNo?: string }
  operation: "select" | "cancel"
}

interface CategoryOption {
  id: string | undefined
  name: string | undefined
}

const props = defineProps<{
  isVisible: boolean // 모달 표시 여부
  // onProductSelected?: (product: { name: string; productNo?: string }) => void; // emit으로 대체
  // onCancel?: () => void; // emit으로 대체
}>()

const emit = defineEmits<{
  (e: "close"): void // 모달 닫힐 때 항상 발생
  (e: "select", payload: { name: string; productNo?: string }): void // 상품 선택 완료 시
}>()

const $style = useCssModule()

// useProductCategory composable 사용
const {
  categories,
  loading,
  error,
  fetchCategories: fetchCategoriesFromComposable, // 이름 충돌 방지를 위해 alias 사용
} = useProductCategory()

// 임시 카테고리 데이터 및 상태 관리 - 제거
// const categories = ref<ProductCategory[]>([]);
// const loading = ref<boolean>(false);
// const error = ref<Error | null>(null);

const fetchCategories = async () => {
  loading.value = true // useProductCategory의 loading 사용
  error.value = null // useProductCategory의 error 사용
  console.log("[SelectProductModal] Fetching categories using composable...")
  await fetchCategoriesFromComposable() // composable의 fetchCategories 호출
  // categories.value는 composable에 의해 업데이트됨
  loading.value = false // 실제로는 composable 내부에서 관리됨. 필요시 조정.
  console.log("[SelectProductModal] Categories loaded via composable:", categories.value)
}

const selectedLevel1CategoryId = ref<string | undefined>(undefined)
const selectedLevel2CategoryId = ref<string | undefined>(undefined)
const selectedLevel3CategoryId = ref<string | undefined>(undefined)

const finalSelectedCategoryInfo = ref<{ id: string; name: string; productNo?: string } | null>(null)

onMounted(async () => {
  // isVisible prop이 true일 때만 카테고리 로드 (모달이 실제로 보여질 때)
  // 또는 항상 로드하도록 할 수도 있음 (선택 사항)
  if (props.isVisible && categories.value.length === 0) {
    await fetchCategories()
  }
  // resetSelections(); // onMounted에서 초기화하면 첫 선택이 안될 수 있음. watch isVisible로 처리
})

watch(
  () => props.isVisible,
  (newValue) => {
    if (newValue) {
      if (categories.value.length === 0) {
        fetchCategories()
      }
      resetSelections() // 모달이 보일 때마다 선택 초기화
    }
  },
)

const resetSelections = () => {
  selectedLevel1CategoryId.value = undefined
  selectedLevel2CategoryId.value = undefined
  selectedLevel3CategoryId.value = undefined
  finalSelectedCategoryInfo.value = null
  console.log("[SelectProductModal] Selections reset.")
}

const level1CategoryOptions = computed<CategoryOption[]>(() => {
  return categories.value.filter((c) => !c.parentId).map((c) => ({ id: c.id, name: c.name }))
})

const level2CategoryOptions = computed<CategoryOption[]>(() => {
  if (!selectedLevel1CategoryId.value) return []
  return categories.value
    .filter((c) => c.parentId === selectedLevel1CategoryId.value)
    .map((c) => ({ id: c.id, name: c.name }))
})

const level3CategoryOptions = computed<CategoryOption[]>(() => {
  if (!selectedLevel2CategoryId.value) return []
  return categories.value
    .filter((c) => c.parentId === selectedLevel2CategoryId.value)
    .map((c) => ({ id: c.id, name: c.name }))
})

watch(selectedLevel1CategoryId, (newCategoryId, oldCategoryId) => {
  if (newCategoryId !== oldCategoryId) {
    selectedLevel2CategoryId.value = undefined
    selectedLevel3CategoryId.value = undefined
    handleCategorySelection(newCategoryId, 1)
  }
})

watch(selectedLevel2CategoryId, (newCategoryId, oldCategoryId) => {
  if (newCategoryId !== oldCategoryId) {
    selectedLevel3CategoryId.value = undefined
    handleCategorySelection(newCategoryId, 2)
  }
})

watch(selectedLevel3CategoryId, (newCategoryId, oldCategoryId) => {
  if (newCategoryId !== oldCategoryId) {
    handleCategorySelection(newCategoryId, 3)
  }
})

const handleCategorySelection = (categoryId: string | undefined, level: number) => {
  console.log(`[SelectProductModal] handleCategorySelection - Level ${level} - categoryId:`, categoryId)
  finalSelectedCategoryInfo.value = null // 선택 변경 시 최종 선택 정보 초기화

  if (categoryId) {
    const category = categories.value.find((c) => c.id === categoryId)
    if (!category) return

    let hasChildren = false
    if (level === 1) {
      hasChildren = level2CategoryOptions.value.length > 0
    } else if (level === 2) {
      hasChildren = level3CategoryOptions.value.length > 0
    }
    // 3단계이거나, 현재 레벨에서 하위 카테고리가 없으면 최종 선택으로 간주
    if (level === 3 || !hasChildren) {
      finalSelectedCategoryInfo.value = {
        id: category.id,
        name: category.name,
        productNo:
          category.productNo !== undefined && category.productNo !== null ? String(category.productNo) : undefined,
      }
      console.log("[SelectProductModal] Final category selected:", finalSelectedCategoryInfo.value)
    }
  }
}

const handleOverlayClick = () => {
  // FeedbackDialog.vue와 동일하게 오버레이 클릭 시 닫기
  closeModal()
}

const closeModal = () => {
  emit("close")
}

const submitSelection = () => {
  if (finalSelectedCategoryInfo.value) {
    emit("select", {
      name: finalSelectedCategoryInfo.value.name,
      productNo: finalSelectedCategoryInfo.value.productNo,
    })
  }
}
</script>

<style module lang="less">
// FeedbackDialog.vue, LoginDialog.vue 스타일 참고

.dialogOverlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000; // 다른 요소 위에 오도록
  backdrop-filter: blur(4px);
}

.dialogContainer {
  background-color: white;
  border-radius: 12px; // LoginDialog.vue 참고
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); // LoginDialog.vue 참고
  width: 90%;
  max-width: 400px; // LoginDialog.vue 참고
  display: flex;
  flex-direction: column;
  overflow: hidden; // 내부 컨텐츠가 넘칠 경우
  animation: fadeIn 0.2s ease-out; // LoginDialog.vue 참고
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.headerSection {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eee; // LoginDialog.vue 참고

  .title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333; // LoginDialog.vue 참고
  }
}

.closeButton {
  background: transparent;
  border: none;
  font-size: 20px; // 크기 조정
  color: #666;
  cursor: pointer;
  padding: 0; // 패딩 조정
  line-height: 1; // 아이콘 정렬
  transition: color 0.2s;

  &:hover {
    color: #333;
  }
}

.contentSection {
  padding: 20px;
  max-height: 60vh; // 내부 컨텐츠 스크롤을 위해
  overflow-y: auto;

  .description {
    margin: 0 0 20px 0;
    color: #555;
    font-size: 14px;
  }
}

.loadingState,
.errorState {
  text-align: center;
  padding: 20px;
  color: #555;
  font-size: 14px;

  .spinner {
    margin: 10px auto;
    width: 24px;
    height: 24px;
    border: 3px solid rgba(0, 0, 0, 0.1);
    border-left-color: #cf1578; // 테마 색상
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.errorState {
  color: #e53e3e; // 에러 색상
}

.categorySelectionArea {
  display: flex;
  flex-direction: column;
  gap: 16px; // 각 폼 그룹 사이 간격
}

.formGroup {
  display: flex;
  flex-direction: column;
  gap: 6px;

  .formLabel {
    font-size: 14px;
    font-weight: 500;
    color: #333;
  }
}

.selectMenu {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background-color: white;
  width: 100%;
  box-sizing: border-box; // 패딩, 보더 포함 크기 계산
  transition: border-color 0.2s;
  appearance: none; // 기본 OS 스타일 제거 (선택적)
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E"); // 커스텀 화살표 (Tailwind UI 참고)
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem; // 화살표 공간 확보

  &:focus {
    outline: none;
    border-color: #cf1578; // 테마 색상
  }
}

.footerSection {
  padding: 16px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #eee;
}

.button {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition:
    opacity 0.2s,
    background-color 0.2s,
    border-color 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.cancelButton {
  background-color: #f5f5f5; // LoginDialog.vue 참고
  color: #333;
  border: 1px solid #ddd;

  &:hover:not(:disabled) {
    background-color: #eee;
  }
}

.submitButton {
  background-color: #cf1578; // LoginDialog.vue 참고 (테마 색상)
  color: white;
  border: 1px solid #cf1578;

  &:hover:not(:disabled) {
    opacity: 0.9;
    background-color: darken(#cf1578, 5%);
    border-color: darken(#cf1578, 5%);
  }
  &:disabled {
    background-color: #e5e7eb; // 비활성화 시 색상 (FeedbackDialog.vue 참고)
    border-color: #e5e7eb;
    color: #9ca3af;
  }
}
</style>
